import React from 'react';

import { ListItemIcon, MenuItem } from '@mui/material';
import qs from 'qs';
import { UseFormSetValue } from 'react-hook-form';
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';

import { IFields } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/ProgramActivityCardModal.types';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/ProgramActivityCardModal/components/SideMenu/SideMenu.module.scss';
import { IStatus } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { CustomizedDropdownMenu, CustomizedRoundIcon, CustomizedTooltip } from 'components/common';

interface ISideMenuProps {
    title: string;
    taskId: number;
    taskVersionHashCode: string;
    originalStatus: IStatus;
    setValue: UseFormSetValue<IFields>;
    category: string;
    originalStartDate: number;
    originalEndDate: number;
    isInterimChanged?: boolean;
}

export const SideMenu = ({
    title,
    taskId,
    taskVersionHashCode,
    originalStatus,
    setValue,
    category,
    originalStartDate,
    originalEndDate,
    isInterimChanged,
}: ISideMenuProps) => {
    const history = useHistory();
    const { path } = useRouteMatch();
    const params = useParams();
    const { search } = useLocation();

    const handleCreateUserAction = (taskId: number) => {
        const newSearch = qs.stringify({ ...qs.parse(search), taskId: taskId });
        const pathname = generatePath(path, { ...params, cardId: 'newCard' });
        setTimeout(() => {
            history.push({
                pathname,
                search: newSearch,
            });
        }, 0);
    };

    const handleRollbackStatusToOriginal = () => {
        setValue('status', { ...originalStatus, originalStatus: originalStatus.name }, { shouldDirty: true });
        setValue('startDate', originalStartDate, { shouldDirty: true });
        setValue('endDate', originalEndDate, { shouldDirty: true });
    };

    const handleOpenCompletionPlan = () => {
        history.push({
            pathname: AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.completionPlan.link),
            search: `?milestoneFragnet=${taskVersionHashCode}`,
        });
    };

    const handleOpenProgressMilestoneFragnet = () => {
        history.push({
            pathname: AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.progressOverview.link),
            search: `?milestoneFragnet=${taskVersionHashCode}`,
        });
    };

    const handleOpenProgressWbs = () => {
        history.push({
            pathname: AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.progressOverview.link),
            search: `?wbs=${taskId}`,
        });
    };

    const handleOpenGannt = () => {
        history.push({
            pathname: AppRoutes.buildPath(AppRoutes.DASHBOARD_PLANNING_PATH, executionConfig.ganttChart.link),
            search: `?milestoneFragnet=${taskVersionHashCode}`,
        });
    };

    return (
        <div className={classes.sideMenuContainer}>
            <ShareInsightButton title={`activity "${title}"`} link={window.location.href} size={'30px'} />

            <CustomizedDropdownMenu
                triggerElement={
                    <CustomizedTooltip
                        tooltipContent={`Manage Activity`}
                        triggerElement={
                            <CustomizedRoundIcon size={'30px'}>
                                <Icon name={'three-dot-icon'} />
                            </CustomizedRoundIcon>
                        }
                    />
                }
            >
                {taskId && (
                    <MenuItem
                        onClick={() => {
                            handleCreateUserAction(taskId);
                        }}
                    >
                        <ListItemIcon>
                            <MuiIcon icon={'add'} />
                        </ListItemIcon>
                        Add Associated User Action
                    </MenuItem>
                )}

                {isInterimChanged && (
                    <MenuItem onClick={handleRollbackStatusToOriginal}>
                        <ListItemIcon>
                            <MuiIcon icon={'settings_backup_restore'} />
                        </ListItemIcon>
                        Rollback Status to Original
                    </MenuItem>
                )}

                {taskVersionHashCode && (
                    <MenuItem onClick={handleOpenCompletionPlan}>
                        <ListItemIcon>
                            <MuiIcon icon={'flaky'} fontSize={'15px'} color={'var(--text-secondary)'} />
                        </ListItemIcon>
                        Open Completion Plan
                    </MenuItem>
                )}

                {taskVersionHashCode && category !== 'WBS' && (
                    <MenuItem onClick={handleOpenProgressMilestoneFragnet}>
                        <ListItemIcon>
                            <MuiIcon
                                icon={'timer'}
                                fontSize={'15px'}
                                type={'outlined'}
                                color={'var(--text-secondary)'}
                            />
                        </ListItemIcon>
                        Open Progress
                    </MenuItem>
                )}
                {taskId && category === 'WBS' && (
                    <MenuItem onClick={handleOpenProgressWbs}>
                        <ListItemIcon>
                            <MuiIcon
                                icon={'timer'}
                                fontSize={'15px'}
                                type={'outlined'}
                                color={'var(--text-secondary)'}
                            />
                        </ListItemIcon>
                        Open Progress
                    </MenuItem>
                )}

                {taskVersionHashCode && (
                    <MenuItem onClick={handleOpenGannt}>
                        <ListItemIcon>
                            <MuiIcon
                                icon={'sort'}
                                fontSize={'15px'}
                                type={'outlined'}
                                color={'var(--text-secondary)'}
                            />
                        </ListItemIcon>
                        Open Gantt
                    </MenuItem>
                )}
            </CustomizedDropdownMenu>
        </div>
    );
};
